import { useEffect, useRef, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { MapPin } from "lucide-react";
import { paths, type AddressMatch } from "@clevis/shared-types";
import { Input, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import { useAuth } from "../hooks/useAuth";
import { PinDrop } from "./PinDrop";

// Every address box: type, pause, and Clevis looks it up (near the
// company); pick the match and it's on the map. No exact match — a new lot
// with no street address yet — is one tap from dropping a pin instead. The
// pin feeds routing and navigation the same as a found address.

export type Located = { lat: number; lng: number; exact: boolean } | null;
/** A saved record's spot on the map (its lat/lng), for the box to start from. */
export const locatedOf = (x: { lat?: number | null; lng?: number | null } | null | undefined): Located => (x && typeof x.lat === "number" && typeof x.lng === "number" ? { lat: x.lat, lng: x.lng, exact: true } : null);

const cache = new Map<string, AddressMatch[]>();
let homeCache: { tenantId: string; p: Promise<{ lat: number; lng: number } | null> } | null = null;
function companyHome(tenantId: string) {
  if (homeCache?.tenantId !== tenantId) {
    homeCache = {
      tenantId,
      p: getDoc(doc(db, paths.tenantRoutingSettings(tenantId)))
        .then((s) => {
          const lat = s.get("homeLat") as number | null | undefined;
          const lng = s.get("homeLng") as number | null | undefined;
          return typeof lat === "number" && typeof lng === "number" ? { lat, lng } : null;
        })
        .catch(() => null),
    };
  }
  return homeCache.p;
}

export function AddressField({
  value,
  onChange,
  located,
  onLocate,
  onPick,
  fill = "full",
  placeholder = "Start typing the address…",
  id,
  className,
  inputClassName,
}: {
  value: string;
  onChange: (text: string) => void;
  /** Where it is on the map now (saved, found, or pinned). */
  located: Located;
  onLocate: (loc: Located) => void;
  /** A match was picked (forms with separate city / state / zip boxes fill them from it). */
  onPick?: (m: AddressMatch) => void;
  /** "street": the box holds only the street (the form has city/state/zip boxes). */
  fill?: "full" | "street";
  placeholder?: string;
  id?: string;
  className?: string;
  inputClassName?: string;
}) {
  const { tenantId } = useAuth();
  const [typed, setTyped] = useState(false);
  const [matches, setMatches] = useState<AddressMatch[] | null>(null);
  const [looking, setLooking] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [home, setHome] = useState<{ lat: number; lng: number } | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    if (tenantId) void companyHome(tenantId).then(setHome);
  }, [tenantId]);

  // Look it up once they stop typing.
  useEffect(() => {
    if (!typed || !tenantId) return;
    const q = value.trim();
    if (q.length < 6 || !/[a-z]/i.test(q)) {
      setMatches(null);
      return;
    }
    const hit = cache.get(q.toLowerCase());
    if (hit) {
      setMatches(hit);
      setListOpen(true);
      return;
    }
    const n = ++seq.current;
    const t = setTimeout(() => {
      setLooking(true);
      call<{ matches: AddressMatch[] }>("lookupAddress", { tenantId, query: q }, { mutation: false })
        .then((r) => {
          cache.set(q.toLowerCase(), r.matches);
          if (n === seq.current) {
            setMatches(r.matches);
            setListOpen(true);
          }
        })
        .catch(() => n === seq.current && setMatches([]))
        .finally(() => n === seq.current && setLooking(false));
    }, 650);
    return () => clearTimeout(t);
  }, [value, typed, tenantId]);

  function pick(m: AddressMatch) {
    onChange(fill === "street" ? m.street || m.address.split(",")[0]! : m.address);
    onLocate({ lat: m.lat, lng: m.lng, exact: m.exact });
    onPick?.(m);
    setTyped(false);
    setListOpen(false);
    setMatches(null);
  }

  const exactFound = (matches ?? []).some((m) => m.exact);
  const nearest = matches?.[0] ?? null;
  return (
    <div className={cn("relative", className)}>
      <Input
        id={id}
        className={inputClassName}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        maxLength={300}
        onChange={(e) => {
          onChange(e.target.value);
          setTyped(true);
          if (located) onLocate(null);
        }}
        onFocus={() => matches?.length && setListOpen(true)}
        onBlur={() => setTimeout(() => setListOpen(false), 150)}
      />
      {listOpen && matches && matches.length ? (
        <div className="absolute left-0 right-0 z-30 mt-1 overflow-hidden rounded-lg bg-white text-sm shadow-lg ring-1 ring-steel/20">
          {matches.map((m) => (
            <button
              key={`${m.lat},${m.lng},${m.address}`}
              type="button"
              className="flex w-full items-start gap-2 px-3 py-1.5 text-left hover:bg-cream"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(m)}
            >
              <MapPin size={14} className={cn("mt-0.5 shrink-0", m.exact ? "text-brandorange" : "text-steel")} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-ink">{m.address}</span>
                {!m.exact ? <span className="block text-[11px] text-steel">Street or area only, not this exact address</span> : null}
              </span>
            </button>
          ))}
          <button
            type="button"
            className="block w-full border-t border-steel/10 px-3 py-1.5 text-left text-xs text-brandorange-dark hover:bg-cream"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setListOpen(false);
              setPinOpen(true);
            }}
          >
            Not here? Drop a pin on the map
          </button>
        </div>
      ) : null}
      <div className="mt-0.5 flex min-h-4 flex-wrap items-center gap-x-2 text-[11px]">
        {located ? (
          <>
            <span className="text-success">{located.exact ? "✓ Found on the map" : "Pinned on the map"}</span>
            <button type="button" className="text-steel underline hover:text-ink" onClick={() => setPinOpen((v) => !v)}>
              {pinOpen ? "Close map" : "Move the pin"}
            </button>
          </>
        ) : looking ? (
          <span className="text-steel">Looking it up…</span>
        ) : typed && matches !== null && !exactFound && value.trim() ? (
          <>
            <span className="text-brandorange-dark">Couldn't find that exact address.</span>
            <button type="button" className="font-semibold text-brandorange-dark underline" onClick={() => setPinOpen(true)}>
              Drop a pin
            </button>
          </>
        ) : value.trim() ? (
          <button type="button" className="text-steel underline hover:text-ink" onClick={() => setPinOpen(true)}>
            Not on the map yet: drop a pin
          </button>
        ) : (
          <button type="button" className="text-steel underline hover:text-ink" onClick={() => setPinOpen(true)}>
            No address yet? Drop a pin
          </button>
        )}
      </div>
      {pinOpen ? (
        <div className="mt-2 rounded-lg border border-steel/20 p-2">
          <PinDrop
            initial={located ? { lat: located.lat, lng: located.lng } : nearest ? { lat: nearest.lat, lng: nearest.lng } : null}
            home={home}
            onSave={(p) => {
              onLocate({ lat: p.lat, lng: p.lng, exact: false });
              setPinOpen(false);
            }}
            onCancel={() => setPinOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
}
